import { formatCompliancePdfDate } from '@/features/compliance-studio/machinery-directive/utils/buildCompliancePdfCoverMarkdown'
import type { TFunction } from 'i18next'
import type {
  PackagingComponent,
  PackagingRecyclabilityAssessmentStoredEntry,
} from '@/shared/hooks/api/packagingSuppliers'
import { resolveRecyclabilityAssessmentStandardsApplied } from './resolveRecyclabilityAssessmentStandardsApplied'

function escapeHtmlForPdfCell(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

function metaTableRow(label: string, valueInnerHtml: string): string {
  return `<tr><td class="pdf-cover-meta-label">${escapeHtmlForPdfCell(label)}</td><td class="pdf-cover-meta-value">${valueInnerHtml}</td></tr>`
}

function displayOrNotAvailable(
  value: string | undefined,
  notAvailable: string
): string {
  const trimmed = value?.trim() ?? ''
  if (trimmed !== '') {
    return trimmed
  }
  return notAvailable
}

export type BuildPackagingRecyclabilityPdfCoverMarkdownParams = {
  translate: TFunction<'packagingSuppliers'>
  locale: string
  itemName: string
  itemDescription: string
  intendedUse: string
  packagingIdentificationNumber: string
  manufacturerName: string
  manufacturerAddress: string
  reportReferenceNo: string
  dateOfAssessmentIso: string | undefined
  components: PackagingComponent[]
  history: PackagingRecyclabilityAssessmentStoredEntry[] | undefined
  standardsAppliedOverride?: string
  testProcedureOverride?: string
}

export function buildPackagingRecyclabilityPdfCoverMarkdown(
  params: BuildPackagingRecyclabilityPdfCoverMarkdownParams
): string {
  const prefix = 'itemDetail.recyclability.document.pdfCover'
  const notAvailable = params.translate(`${prefix}.notAvailable`)

  const reportReferenceDisplay = displayOrNotAvailable(
    params.reportReferenceNo,
    notAvailable
  )
  const packagingNameDisplay = displayOrNotAvailable(
    params.itemName,
    notAvailable
  )
  const packagingDescriptionDisplay = displayOrNotAvailable(
    params.itemDescription,
    notAvailable
  )
  const packagingIdentificationNumberDisplay = displayOrNotAvailable(
    params.packagingIdentificationNumber,
    notAvailable
  )
  const intendedUseDisplay = displayOrNotAvailable(
    params.intendedUse,
    notAvailable
  )
  const manufacturerNameDisplay = displayOrNotAvailable(
    params.manufacturerName,
    notAvailable
  )
  const manufacturerAddressDisplay = displayOrNotAvailable(
    params.manufacturerAddress,
    notAvailable
  )

  const standardsAppliedDisplay = displayOrNotAvailable(
    params.standardsAppliedOverride ??
      resolveRecyclabilityAssessmentStandardsApplied({
        components: params.components,
        history: params.history,
      }),
    notAvailable
  )

  const testProcedureDisplay = displayOrNotAvailable(
    params.testProcedureOverride ??
      params.translate(`${prefix}.testProcedureValue`),
    notAvailable
  )

  const dateFormatted =
    params.dateOfAssessmentIso != null
      ? formatCompliancePdfDate(params.dateOfAssessmentIso, params.locale)
      : notAvailable

  const rows = [
    metaTableRow(
      params.translate(`${prefix}.reportReferenceNo`),
      escapeHtmlForPdfCell(reportReferenceDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.dateOfAssessment`),
      escapeHtmlForPdfCell(dateFormatted)
    ),
    metaTableRow(
      params.translate(`${prefix}.packagingName`),
      escapeHtmlForPdfCell(packagingNameDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.packagingDescription`),
      escapeHtmlForPdfCell(packagingDescriptionDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.packagingIdentificationNumber`),
      escapeHtmlForPdfCell(packagingIdentificationNumberDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.intendedUse`),
      escapeHtmlForPdfCell(intendedUseDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.manufacturerName`),
      escapeHtmlForPdfCell(manufacturerNameDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.manufacturerAddress`),
      escapeHtmlForPdfCell(manufacturerAddressDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.standardsApplied`),
      escapeHtmlForPdfCell(standardsAppliedDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.testProcedure`),
      escapeHtmlForPdfCell(testProcedureDisplay)
    ),
    metaTableRow(
      params.translate(`${prefix}.preparedBy`),
      escapeHtmlForPdfCell('')
    ),
    metaTableRow(
      params.translate(`${prefix}.approvedBy`),
      escapeHtmlForPdfCell('')
    ),
  ].join('\n')

  const table = `<div class="pdf-cover-meta-table-wrap">
<table class="pdf-cover-meta-table">
<tbody>
${rows}
</tbody>
</table>
</div>`

  return [
    `# ${params.translate(`${prefix}.documentTitle`)}`,
    '',
    `## ${params.translate(`${prefix}.subtitle`)}`,
    '',
    params.translate(`${prefix}.description`),
    '',
    table,
  ].join('\n')
}
